import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, resolveImg, uploadFile } from "@/lib/api";
import { useAuth, isManager, isDesigner } from "@/context/AuthContext";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PhaseFormDialog } from "@/components/shared/PhaseFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PriorityBadge, TaskStatusBadge } from "@/components/TaskBadges";
import { ProjectEconomics, projectEcoQuery } from "@/components/ProjectEconomics";
import { ProjectDiary } from "@/components/ProjectDiary";
import { ProjectBudget } from "@/components/ProjectBudget";
import { ArrowLeft, Flag, Plus, Users, Pencil, ClipboardList, Paperclip, Trash2, AlertTriangle, Check } from "lucide-react";
import { ProjectEditDialog } from "@/components/ProjectEditDialog";
import { useOpenPhasesGuard } from "@/components/projects/OpenPhasesGuard";
import TaskDetailModal from "@/components/TaskDetailModal";
import { ProjectHeader, ProjectStatusStrip } from "@/components/ProjectHeader";
import { ProjectInfoTab } from "@/components/ProjectInfoTab";
import { ProjectBoard } from "@/components/ProjectBoard";
import { ProjectAttendanceTab } from "@/components/ProjectAttendanceTab";
import { ProjectTeamTab } from "@/components/ProjectTeamTab";
import { ProjectPasportTab } from "@/components/ProjectPasportTab";
import { ProjectDocsTab } from "@/components/ProjectDocsTab";
import { ProjectTasksTab } from "@/components/ProjectTasksTab";
import { useIsNarrow, useScrollTabs, TAB_SHELL, TAB_SCROLL, TAB_BTN } from "@/components/shared/PagePrimitives";
import { toast } from "sonner";
import { DAY, toDate, fmtISO, addDays, startOfDay, addWorkingDays } from "@/lib/days";
import { ask } from "@/lib/confirm";
import { autoPill } from "@/components/shared/Pill";

const STATUSES = ["Poptávka", "Nabídka", "Smlouva podepsána", "V realizaci", "Dokončeno", "Fakturováno", "Záruka"];

const CZ_MONTHS = ["led", "úno", "bře", "dub", "kvě", "čvn", "čvc", "srp", "zář", "říj", "lis", "pro"];
const CZ_DOW = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
const TASK_DONE = ["Hotovo", "Schváleno"];

function phaseTaskStats(phaseId, tasks) {
  const pt = (tasks || []).filter((t) => t.phase_id === phaseId);
  const done = pt.filter((t) => TASK_DONE.includes(t.status)).length;
  return { total: pt.length, done };
}
function effProgress(phase, tasks) {
  const { total, done } = phaseTaskStats(phase.id, tasks);
  if (total > 0) return Math.round((done / total) * 100);
  return Math.round(phase.progress || 0);
}
function projectProgress(phases, tasks) {
  let wsum = 0, tot = 0;
  for (const ph of phases) {
    const days = Math.max(1, (toDate(ph.end_date) - toDate(ph.start_date)) / DAY + 1);
    wsum += effProgress(ph, tasks) * days;
    tot += days;
  }
  return tot ? Math.round(wsum / tot) : 0;
}

const ROW_H = 36, ROW_GAP = 8, TOP_PAD = 12;
const rowCenterY = (i) => TOP_PAD + i * (ROW_H + ROW_GAP) + ROW_H / 2;

function InteractiveGantt({ phases, tasks, editable, onSave, onCreate, onOpenPhase }) {
  const wrapRef = useRef(null);
  const barsRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [draft, setDraft] = useState(null); // {id, start, end}
  const [ghost, setGhost] = useState(null); // {start, end, rowIdx}
  const dragRef = useRef(null);
  const curDraftRef = useRef(null);
  const createRef = useRef(null);
  const lpTimer = useRef(null);

  useEffect(() => {
    const measure = () => wrapRef.current && setWidth(wrapRef.current.offsetWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [phases]);

  if (!phases.length) return <p className="text-muted-foreground text-sm">Zatím žádné fáze.</p>;

  const dates = phases.flatMap((p) => [toDate(p.start_date), toDate(p.end_date)]);
  const min = addDays(new Date(Math.min(...dates)), -3);
  const max = addDays(new Date(Math.max(...dates)), 3);
  const span = Math.max((max - min) / DAY, 1);
  const pxPerDay = width / span;
  const today = new Date();
  const todayPct = ((today - min) / DAY / span) * 100;
  const dayMode = pxPerDay >= 30;

  const getPhase = (ph) => (draft && draft.id === ph.id ? { ...ph, start_date: draft.start, end_date: draft.end } : ph);

  // Month labels
  const monthTicks = [];
  let mIt = new Date(min.getFullYear(), min.getMonth(), 1);
  while (mIt <= max) {
    monthTicks.push({ key: +mIt, leftPct: ((mIt - min) / DAY / span) * 100, label: `${CZ_MONTHS[mIt.getMonth()]} ${mIt.getFullYear()}` });
    mIt = new Date(mIt.getFullYear(), mIt.getMonth() + 1, 1);
  }

  // Day cells (only when zoomed in) — day-name + number + weekend shading
  const dayCells = [];
  if (dayMode) {
    for (let t = startOfDay(min); t <= max; t = addDays(t, 1)) {
      const leftPct = ((t - min) / DAY / span) * 100;
      if (leftPct < 0 || leftPct > 100) continue;
      const dow = t.getDay();
      dayCells.push({ key: +t, leftPct, wPct: (1 / span) * 100, dow, weekend: dow === 0 || dow === 6, label: `${CZ_DOW[dow]} ${t.getDate()}` });
    }
  }
  // Week ticks (Mondays) when zoomed out
  const weekTicks = [];
  if (!dayMode) {
    let t = startOfDay(min);
    while (t.getDay() !== 1) t = addDays(t, 1);
    for (; t <= max; t = addDays(t, 7)) {
      const leftPct = ((t - min) / DAY / span) * 100;
      if (leftPct >= 0 && leftPct <= 100) weekTicks.push({ key: +t, leftPct, label: `${t.getDate()}.${t.getMonth() + 1}.` });
    }
  }

  // Geometry per phase (for dependency arrows)
  const geo = {};
  phases.forEach((raw, i) => {
    const ph = getPhase(raw);
    geo[raw.id] = {
      i,
      sPct: ((toDate(ph.start_date) - min) / DAY / span) * 100,
      ePct: ((toDate(ph.end_date) - min) / DAY / span) * 100,
      start: toDate(ph.start_date), end: toDate(ph.end_date),
    };
  });
  const barsHeight = TOP_PAD + phases.length * (ROW_H + ROW_GAP);

  const arrows = [];
  const conflictSet = new Set();
  phases.forEach((raw) => {
    const dep = raw.depends_on;
    if (!dep || !geo[dep] || dep === raw.id) return;
    const from = geo[dep], to = geo[raw.id];
    const conflict = to.start < from.end;
    if (conflict) conflictSet.add(raw.id);
    arrows.push({
      id: raw.id, conflict,
      x1: (from.ePct / 100) * width, y1: rowCenterY(from.i),
      x2: (to.sPct / 100) * width, y2: rowCenterY(to.i),
    });
  });

  const onDown = (e, ph, mode) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { id: ph.id, mode, startX: e.clientX, start: toDate(ph.start_date), end: toDate(ph.end_date) };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaDays = Math.round((ev.clientX - d.startX) / pxPerDay);
      let s = new Date(d.start), en = new Date(d.end);
      if (d.mode === "move") { s = addDays(d.start, deltaDays); en = addDays(d.end, deltaDays); }
      else if (d.mode === "start") { s = addDays(d.start, deltaDays); if (s > addDays(d.end, -1)) s = addDays(d.end, -1); }
      else if (d.mode === "end") { en = addDays(d.end, deltaDays); if (en < addDays(d.start, 1)) en = addDays(d.start, 1); }
      setDraft({ id: d.id, start: fmtISO(s), end: fmtISO(en) });
      curDraftRef.current = { id: d.id, start: fmtISO(s), end: fmtISO(en) };
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      const d = dragRef.current;
      dragRef.current = null;
      const cur = curDraftRef.current;
      curDraftRef.current = null;
      setDraft(null);
      if (d && cur && cur.id === d.id && (cur.start !== fmtISO(d.start) || cur.end !== fmtISO(d.end))) {
        onSave(d.id, { start_date: cur.start, end_date: cur.end });
      } else if (d && !cur && d.mode === "move" && onOpenPhase) {
        onOpenPhase(d.id);   // stisk bez tažení = klik → otevřít detail fáze (stejně jako v Plánování)
      }
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const finishCreate = (c) => {
    setGhost(null);
    if (!c) return;
    if (c.moved && c.range) onCreate(c.range.start, c.range.end);
    else { const s = fmtISO(addDays(min, c.startDay)); onCreate(s, addWorkingDays(s, 5)); }
  };
  const onCreateDown = (e) => {
    if (!editable || !onCreate || e.button !== 0) return;
    const rect = barsRef.current.getBoundingClientRect();
    const startDay = Math.max(0, Math.round((e.clientX - rect.left) / pxPerDay));
    const rowIdx = Math.max(0, Math.min(phases.length - 1, Math.floor((e.clientY - rect.top - TOP_PAD) / (ROW_H + ROW_GAP))));
    createRef.current = { startDay, rowIdx, moved: false, range: null };
    const move = (ev) => {
      const c = createRef.current; if (!c) return;
      const endDay = Math.max(0, Math.round((ev.clientX - rect.left) / pxPerDay));
      if (Math.abs(endDay - c.startDay) >= 1) c.moved = true;
      const a = Math.min(c.startDay, endDay), b = Math.max(c.startDay, endDay);
      c.range = { start: fmtISO(addDays(min, a)), end: fmtISO(addDays(min, b)) };
      setGhost({ ...c.range, rowIdx: c.rowIdx });
    };
    const up = () => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up);
      const c = createRef.current; createRef.current = null; finishCreate(c);
    };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
  };
  const onCreateTouchStart = (e) => {
    if (!editable || !onCreate || e.touches.length !== 1) return;
    const t = e.touches[0], sx = t.clientX, sy = t.clientY;
    const rect = barsRef.current.getBoundingClientRect();
    const startDay = Math.max(0, Math.round((sx - rect.left) / pxPerDay));
    const rowIdx = Math.max(0, Math.min(phases.length - 1, Math.floor((sy - rect.top - TOP_PAD) / (ROW_H + ROW_GAP))));
    const cleanup = () => { document.removeEventListener("touchmove", move); document.removeEventListener("touchend", end); };
    const move = (ev) => {
      const tt = ev.touches[0]; if (!tt) return;
      if (!createRef.current) {
        if (Math.abs(tt.clientX - sx) > 10 || Math.abs(tt.clientY - sy) > 10) { clearTimeout(lpTimer.current); cleanup(); }
        return;
      }
      ev.preventDefault();
      const endDay = Math.max(0, Math.round((tt.clientX - rect.left) / pxPerDay));
      if (Math.abs(endDay - createRef.current.startDay) >= 1) createRef.current.moved = true;
      const a = Math.min(createRef.current.startDay, endDay), b = Math.max(createRef.current.startDay, endDay);
      createRef.current.range = { start: fmtISO(addDays(min, a)), end: fmtISO(addDays(min, b)) };
      setGhost({ ...createRef.current.range, rowIdx: createRef.current.rowIdx });
    };
    const end = () => { clearTimeout(lpTimer.current); const c = createRef.current; createRef.current = null; cleanup(); finishCreate(c); };
    lpTimer.current = setTimeout(() => {
      createRef.current = { startDay, rowIdx, moved: false, range: null };
      setGhost({ start: fmtISO(addDays(min, startDay)), end: fmtISO(addDays(min, startDay)), rowIdx });
    }, 400);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end);
  };

  return (
    <div className="overflow-x-auto">
      <div ref={wrapRef} className="relative min-w-[560px] select-none" data-testid="gantt-chart">
        {/* background: weekend shading + gridlines + today line */}
        <div className="absolute inset-0 pointer-events-none">
          {dayCells.filter((d) => d.weekend).map((d) => (
            <div key={`we-${d.key}`} className="absolute top-0 bottom-0 bg-muted-foreground/10" style={{ left: `${d.leftPct}%`, width: `${d.wPct}%` }} />
          ))}
          {dayMode && dayCells.map((t) => (
            <div key={t.key} className="absolute top-0 bottom-0 w-px bg-border/50" style={{ left: `${t.leftPct}%` }} />
          ))}
          {monthTicks.map((t) => (
            t.leftPct >= 0 && t.leftPct <= 100 ? <div key={t.key} className="absolute top-0 bottom-0 w-px bg-border" style={{ left: `${t.leftPct}%` }} /> : null
          ))}
          {todayPct >= 0 && todayPct <= 100 && (
            <div className="absolute top-0 bottom-0 w-px bg-brand z-10" style={{ left: `${todayPct}%` }} />
          )}
        </div>

        {/* time axis header */}
        <div className="relative h-10 border-b border-border">
          <div className="relative h-5">
            {monthTicks.map((t) => (
              <span key={t.key} className="absolute top-0 text-[11px] font-bold uppercase tracking-wide text-foreground whitespace-nowrap"
                style={{ left: `${Math.max(t.leftPct, 0.5)}%` }}>{t.label}</span>
            ))}
          </div>
          <div className="relative h-5">
            {dayMode ? dayCells.map((t) => (
              <span key={t.key} className={`absolute top-0 -translate-x-1/2 text-[10px] whitespace-nowrap ${t.weekend ? "text-muted-foreground/70 font-semibold" : "text-muted-foreground"}`}
                style={{ left: `${t.leftPct + t.wPct / 2}%` }}>{t.label}</span>
            )) : weekTicks.map((t) => (
              <span key={t.key} className="absolute top-0 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap" style={{ left: `${t.leftPct}%` }}>{t.label}</span>
            ))}
            {todayPct >= 0 && todayPct <= 100 && (
              <span className="absolute top-0 -translate-x-1/2 text-[10px] text-brand font-bold" style={{ left: `${todayPct}%` }}>dnes</span>
            )}
          </div>
        </div>

        {/* bars + dependency arrows */}
        <div ref={barsRef} className="relative pt-3" style={{ height: barsHeight }}
          onMouseDown={onCreateDown} onTouchStart={onCreateTouchStart}>
          {ghost && (
            <div className="absolute rounded-sm border-2 border-dashed border-brand bg-brand/20 pointer-events-none flex items-center px-2 z-30"
              style={{ top: TOP_PAD + ghost.rowIdx * (ROW_H + ROW_GAP), height: ROW_H,
                left: `${((toDate(ghost.start) - min) / DAY / span) * 100}%`,
                width: `${Math.max(((toDate(ghost.end) - toDate(ghost.start)) / DAY + 1) / span * 100, 1)}%` }}
              data-testid="gantt-create-ghost">
              <span className="text-[10px] font-bold text-brand truncate">Nová fáze · {formatDate(ghost.start)}–{formatDate(ghost.end)}</span>
            </div>
          )}
          <svg className="absolute inset-0 pointer-events-none z-20" width={width} height={barsHeight} style={{ overflow: "visible" }}>
            {arrows.map((a) => {
              const midX = a.x1 + 14;
              const d = `M ${a.x1} ${a.y1} H ${midX} V ${a.y2} H ${a.x2}`;
              return (
                <g key={`arr-${a.id}`} className={a.conflict ? "text-destructive" : "text-brand"}>
                  <path d={d} stroke="currentColor" strokeWidth="1.5" fill="none" strokeDasharray={a.conflict ? "4 3" : undefined} />
                  <polygon points={`${a.x2},${a.y2} ${a.x2 - 7},${a.y2 - 4} ${a.x2 - 7},${a.y2 + 4}`} fill="currentColor" />
                </g>
              );
            })}
          </svg>
          {phases.map((raw, i) => {
            const ph = getPhase(raw);
            const s = ((toDate(ph.start_date) - min) / DAY / span) * 100;
            const w = ((toDate(ph.end_date) - toDate(ph.start_date)) / DAY / span) * 100;
            const prog = effProgress(raw, tasks);
            const done = prog >= 100;
            const isConflict = conflictSet.has(raw.id);
            return (
              <div key={raw.id} className="absolute h-9" style={{ top: TOP_PAD + i * (ROW_H + ROW_GAP), left: 0, right: 0 }}>
                <div data-testid={`gantt-bar-${raw.id}`}
                  onMouseDown={(e) => onDown(e, ph, "move")}
                  title={editable ? "Táhněte pro posun, klikněte pro úpravu" : undefined}
                  className={`absolute inset-y-0 rounded-sm border overflow-hidden flex items-center px-2 ${done ? "bg-emerald-500/15 border-emerald-500" : isConflict ? "bg-secondary border-destructive" : "bg-secondary border-border"} ${editable ? "cursor-grab active:cursor-grabbing hover:border-brand" : ""}`}
                  style={{ left: `${s}%`, width: `${Math.max(w, 6)}%` }}>
                  <div className={`absolute inset-y-0 left-0 pointer-events-none ${done ? "bg-emerald-500/40" : "bg-brand/30"}`} style={{ width: `${prog}%` }} />
                  <span className="relative text-xs font-semibold truncate pointer-events-none">{ph.name} · {prog}%</span>
                  {editable && (
                    <>
                      <div onMouseDown={(e) => onDown(e, ph, "start")} className="absolute left-0 inset-y-0 w-2 cursor-ew-resize bg-brand/40 hover:bg-brand z-10" data-testid={`gantt-start-${raw.id}`} />
                      <div onMouseDown={(e) => onDown(e, ph, "end")} className="absolute right-0 inset-y-0 w-2 cursor-ew-resize bg-brand/40 hover:bg-brand z-10" data-testid={`gantt-end-${raw.id}`} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {conflictSet.size > 0 && (
        <p className="text-xs text-destructive mt-3" data-testid="gantt-conflict-warning">Pozor: některá fáze začíná před koncem fáze, na které závisí (červená šipka).</p>
      )}
      {editable && <p className="text-xs text-muted-foreground mt-1">Tip: táhněte pruh pro posun, okraje pro změnu termínů. Tažením v prázdném řádku (na mobilu dlouhý stisk) vytvoříte novou fázi.</p>}
    </div>
  );
}

// Odznak tabu Pasport (varianta 1a): drží si vlastní barvu a na terakotovém
// aktivním tabu se obrací — jinak bílá fajfka na bílém podkladu zanikne.
// Zelená = hotovo, terakota = zbývá; na terakotě je odznak bílý a barvu nese glyf.
function PasportTabBadge({ total, pending, active }) {
  if (!total) return null;
  const base = "ml-1.5 inline-flex items-center justify-center h-[22px] min-w-[22px] rounded-full font-bold text-[12px] leading-none tabular-nums shrink-0";
  if (pending === 0) {
    return (
      <span role="img" aria-label="Vše objednáno" title="Vše objednáno" data-testid="tab-pasport-done"
        className={`${base} ${active ? "bg-white text-success" : "bg-success text-white"}`}>
        <Check size={13} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span aria-label={`Zbývá objednat ${pending}`} title={`Zbývá objednat ${pending}`}
      data-testid="tab-pasport-pending"
      className={`${base} w-auto px-1.5 ${active ? "bg-white/90 text-brand" : "bg-brand/15 text-brand"}`}>
      {pending > 99 ? "99+" : pending}
    </span>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "info";
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const manager = isManager(user);
  const qc = useQueryClient();   // 220: ekonomika zakázky sdílená se záložkou Ekonomika
  // Projektant (173): čte harmonogram, historii, docházku (bez peněz), píše deník; nic neupravuje.
  const designer = isDesigner(user);
  const reader = manager || designer;
  const [proj, setProj] = useState(null);
  // Odznak tabu Pasport: server dodá výchozí hodnotu, dál stav řídí sám pasport.
  const [pasportCounts, setPasportCounts] = useState(null);
  useEffect(() => { if (proj?.pasport) setPasportCounts(proj.pasport); }, [proj]);
  const [history, setHistory] = useState([]);
  const [phases, setPhases] = useState([]);
  const [eco, setEco] = useState(null);
  const [live, setLive] = useState(null);
  const [team, setTeam] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [milestone, setMilestone] = useState({ title: "", date: "", note: "" });
  const [msOpen, setMsOpen] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ name: "", start_date: "", end_date: "", progress: 0, depends_on: "" });
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [manProg, setManProg] = useState(null);   // ruční % hotovo během tažení posuvníku
  const [phaseEditOpen, setPhaseEditOpen] = useState(false);
  const [phaseEditForm, setPhaseEditForm] = useState({});
  const [modalTask, setModalTask] = useState(null);
  const [modalTaskIds, setModalTaskIds] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);
  const [msBusy, guardMs] = useSubmitGuard();       // milník
  const [ptBusy, guardPt] = useSubmitGuard();       // úkol ve fázi
  const [tasks, setTasks] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskPhaseId, setTaskPhaseId] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", assignees: [], deadline: "", priority: "Střední" });
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  // Klíč obsahuje i načtení zakázky — lišta existuje až po něm.
  const tabsScroll = useScrollTabs(`${tab}|${!!proj}`);
  // Přepínač Události ↔ Úkoly uvnitř záložky (query param `sub`, ať jde odkazovat).
  const [sub, setSub] = useState(searchParams.get("sub") === "tasks" ? "tasks" : "events");
  const narrow = useIsNarrow();
  const coverRef = useRef();
  const msFileRef = useRef();

  // Povolené záložky podle role. Bez tohohle by odkaz s ?tab=docs dělníkovi
  // vykreslil obsah, který v liště nemá. (Nad early-returnem — hook musí běžet vždy.)
  useEffect(() => {
    const allowed = manager
      ? ["info", "schedule", "diary", "attendance", "budget", "eco", "docs", "pasport", "timeline", "team"]
      : designer
      ? ["info", "schedule", "diary", "attendance", "pasport", "timeline", "team"]
      : ["info", "pasport", "team"];
    if (!allowed.includes(tab)) { setTab("info"); setSearchParams({ tab: "info" }, { replace: true }); }
  }, [tab, manager, designer]); // eslint-disable-line

  const load = useCallback(async () => {
    try {
      const [p, h, ph] = await Promise.all([
        api.get(`/projects/${id}`),
        isManager(user) || isDesigner(user) ? api.get(`/projects/${id}/history`) : Promise.resolve({ data: [] }),
        isManager(user) || isDesigner(user) ? api.get(`/projects/${id}/phases`) : Promise.resolve({ data: [] }),
      ]);
      setProj(p.data); setHistory(h.data); setPhases(ph.data);
      // Když se rovnou otevře záložka Historie, hned ji označ jako přečtenou.
      if (initialTab === "timeline" && p.data?.history_unread) {
        api.post(`/projects/${id}/history/seen`).catch(() => {});
        setProj((prev) => (prev ? { ...prev, history_unread: 0 } : prev));
      }
      api.get(`/tasks?project_id=${id}`).then((r) => setTasks(r.data)).catch(() => {});
      if (isManager(user)) {
        qc.fetchQuery({ ...projectEcoQuery(id), staleTime: 0 }).then(setEco).catch(() => {});
        api.get(`/projects/${id}/live`).then((r) => setLive(r.data)).catch(() => {});
        api.get("/team").then((r) => setTeam(r.data));
        api.get("/investors").then((r) => setInvestors(r.data || [])).catch(() => {});
      }
    } catch (e) {
      toast.error("Zakázka nenalezena."); nav("/zakazky");
    }
  }, [id, qc]);

  useEffect(() => { load(); }, [load]);
  const phasesGuard = useOpenPhasesGuard();   // 342: ukončení zakázky s nedokončenými fázemi (hook před return)
  if (!proj) return <div className="text-muted-foreground">Načítám…</div>;

  // Nástěnku píše majitel a asistentka kdekoli, stavbyvedoucí jen na zakázce,
  // kterou má na starost nebo je v jejím týmu. Stejné pravidlo drží i server —
  // tohle je tu proto, aby tlačítko nesvítilo tam, kde by skončilo 403.
  const canEditBoard = manager && (user?.role !== "MANAGER"
    || proj.site_manager_id === user.id || (proj.team || []).includes(user.id));

  const responsibles = team.filter((u) => u.status !== "inactive");
  const respName = (uid) => (team.find((u) => u.id === uid) || {}).name;

  const projectPayload = (over = {}) => ({
    name: proj.name, code: proj.code, address: proj.address,
    street: proj.street, zip: proj.zip, city: proj.city,
    lat: proj.lat, lng: proj.lng,
    investor_id: proj.investor_id, status: proj.status, contract_price: proj.contract_price,
    planned_budget: proj.planned_budget, area_m2: proj.area_m2, start_date: proj.start_date, end_date: proj.end_date,
    description: proj.description, cover_photo: proj.cover_photo,
    // bez `team` (233): tým se mění jen přes /team, jinak by uložení vrátilo cizí změny týmu
    site_manager_id: proj.site_manager_id || null, ...over,
  });

  const openEdit = () => setEditOpen(true);

  // Historie: otevření záložky = přečteno (vynuluje badge nepřečtených událostí).
  const markHistorySeen = () => {
    if (!proj?.history_unread) return;
    setProj((p) => (p ? { ...p, history_unread: 0 } : p));
    api.post(`/projects/${id}/history/seen`).catch(() => {});
  };

  // 263: záložky jen přepisují adresu (replace) — dřív každá přidala krok do historie a Zpět pak
  // přepínal záložky místo návratu tam, odkud člověk přišel
  const changeTab = (v) => { setTab(v); setSearchParams(v === "schedule" ? { tab: v, sub } : { tab: v }, { replace: true }); if (v === "timeline") markHistorySeen(); };
  const changeSub = (v) => { setSub(v); setSearchParams({ tab: "schedule", sub: v }, { replace: true }); };

  const handleMsFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const url = await uploadFile(f); setMilestone((m) => ({ ...m, file_url: url })); toast.success("Příloha nahrána."); }
    catch { toast.error("Nahrání selhalo."); }
    finally { setUploading(false); }
  };

  const openAddTask = (phaseId) => {
    setTaskPhaseId(phaseId);
    setTaskForm({ title: "", assignees: [], deadline: "", priority: "Střední" });
    setTaskOpen(true);
  };

  const submitTask = async () => {
    if (!taskForm.title) return toast.error("Vyplňte název úkolu.");
    await api.post("/tasks", { title: taskForm.title, project_id: id, phase_id: taskPhaseId,
      assignees: taskForm.assignees, deadline: taskForm.deadline || null, priority: taskForm.priority });
    toast.success("Úkol přidán."); setTaskOpen(false); load();
  };

  const toggleTaskAssignee = (uid) => setTaskForm((f) => ({ ...f, assignees: f.assignees.includes(uid) ? f.assignees.filter((x) => x !== uid) : [...f.assignees, uid] }));

  const savePhaseDates = async (phaseId, dates) => {
    const ph = phases.find((p) => p.id === phaseId);
    if (!ph) return;
    setPhases((prev) => prev.map((p) => (p.id === phaseId ? { ...p, ...dates } : p)));
    try {
      await api.patch(`/projects/${id}/phases/${phaseId}`, {
        name: ph.name, start_date: dates.start_date, end_date: dates.end_date,
        progress: ph.progress, depends_on: ph.depends_on,
        responsible_ids: ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : []),
      });
      toast.success("Termíny fáze upraveny.");
    } catch (e) { toast.error("Uložení selhalo."); load(); }
  };

  const deletePhase = async (phaseId) => {
    if (!await ask("Opravdu smazat fázi?")) return;
    await api.delete(`/projects/${id}/phases/${phaseId}`);
    toast.success("Fáze smazána."); load();
  };

  const changeStatus = async (status) => {
    try {
      await phasesGuard.run((x) => api.post(`/projects/${id}/status`, { status, ...x }));
    } catch (e) {
      if (!e.cancelled) toast.error(formatApiError(e.response?.data?.detail));
      return;
    }
    toast.success("Stav změněn."); load();
  };

  const addMilestone = async () => {
    if (!milestone.title || !milestone.date) return toast.error("Vyplňte název a datum.");
    await api.post(`/projects/${id}/history`, milestone);
    toast.success("Milník přidán."); setMsOpen(false); setMilestone({ title: "", date: "", note: "", file_url: null }); load();
  };

  const addPhase = async () => {
    if (!phaseForm.name || !phaseForm.start_date || !phaseForm.end_date) return toast.error("Vyplňte všechna pole.");
    if (!(phaseForm.responsible_ids || []).length) return toast.error("Vyberte aspoň jednoho člověka.");
    await api.post(`/projects/${id}/phases`, {
      name: phaseForm.name, start_date: phaseForm.start_date, end_date: phaseForm.end_date,
      progress: Number(phaseForm.progress) || 0, depends_on: phaseForm.depends_on || null,
      responsible_ids: phaseForm.responsible_ids,
    });
    toast.success("Fáze přidána."); setPhaseOpen(false); setPhaseForm({ name: "", start_date: "", end_date: "", progress: 0, depends_on: "", responsible_ids: [] }); load();
  };

  const openEditPhase = (ph) => {
    setPhaseEditForm({
      id: ph.id, name: ph.name || "",
      start_date: (ph.start_date || "").slice(0, 10), end_date: (ph.end_date || "").slice(0, 10),
      depends_on: ph.depends_on || "", progress: Number(ph.progress) || 0,
      responsible_ids: ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : []),
    });
    setPhaseEditOpen(true);
  };

  const savePhaseEdit = async () => {
    const f = phaseEditForm;
    if (!f.name || !f.start_date || !f.end_date) return toast.error("Vyplňte všechna pole.");
    if (!(f.responsible_ids || []).length) return toast.error("Vyberte aspoň jednoho člověka.");
    if (f.depends_on && f.depends_on === f.id) return toast.error("Fáze nemůže záviset sama na sobě.");
    await api.patch(`/projects/${id}/phases/${f.id}`, {
      name: f.name, start_date: f.start_date, end_date: f.end_date,
      progress: Number(f.progress) || 0, depends_on: f.depends_on || null,
      responsible_ids: f.responsible_ids,
    });
    toast.success("Fáze upravena."); setPhaseEditOpen(false); load();
  };

  // 233: mění se jen ten jeden člověk (server $addToSet/$pull) — rychlé klikání
  // ani souběžná úprava zakázky jiným člověkem se už nepřepíšou.
  const toggleTeam = async (uid, checked) => {
    setProj((p) => ({ ...p, team: checked ? [...new Set([...(p.team || []), uid])] : (p.team || []).filter((x) => x !== uid) }));
    try { await api.patch(`/projects/${id}/team/${uid}`, { member: !!checked }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    load();
  };

  // Hromadné přiřazení — u velkého týmu ušetří klikání po jednom.
  const setAllTeam = async (add) => {
    setTeamBusy(true);
    try {
      await api.put(`/projects/${id}/team`, { team: add ? team.map((u) => u.id) : [] });
      toast.success(add ? "Všichni přiřazeni k zakázce." : "Tým vyprázdněn.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTeamBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <button onClick={() => (location.state?.from ? nav(location.state.from) : location.key === "default" ? nav("/zakazky") : nav(-1))} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm"><ArrowLeft size={16} /> Zpět</button>

      <ProjectHeader proj={proj} manager={manager}
        statuses={STATUSES} onEdit={openEdit} onStatusChange={changeStatus} />

      {phasesGuard.dialog}
      <ProjectEditDialog open={editOpen} onOpenChange={setEditOpen} projectId={id} proj={proj}
        team={team} investors={investors} buildPayload={projectPayload} onSaved={load} onDeleted={() => nav("/zakazky")} />

      <Tabs value={tab} onValueChange={changeTab}>
        <div className={TAB_SHELL}>
          <TabsList ref={tabsScroll.ref} onScroll={tabsScroll.onScroll} style={tabsScroll.style}
            className={`${TAB_SCROLL} w-full justify-start h-auto p-0 bg-transparent md:flex-wrap md:overflow-visible`}>
            {[
              { v: "info", label: "Informace" },
              ...(manager ? [
                { v: "schedule", label: "Události a úkoly" },
                { v: "diary", label: "Deník" },
                { v: "attendance", label: "Docházka" },
                { v: "budget", label: "Rozpočet" },
                { v: "eco", label: "Ekonomika" },
                { v: "docs", label: "Dokumenty" },
              ] : designer ? [
                { v: "schedule", label: "Harmonogram a úkoly" },
                { v: "diary", label: "Deník" },
                { v: "attendance", label: "Docházka" },
              ] : []),
              { v: "pasport", label: "Pasport" },
              ...(reader ? [{ v: "timeline", label: "Historie", badge: proj.history_unread }] : []),
              { v: "team", label: "Tým" },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}`}
                className={`${TAB_BTN} text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground`}
              >
                {t.label}
                {t.badge > 0 && (
                  <span data-testid="tab-timeline-unread" className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold inline-flex items-center justify-center tabular-nums">
                    {t.badge > 99 ? "99+" : t.badge}
                  </span>
                )}
                {t.v === "pasport" && (
                  <PasportTabBadge total={pasportCounts?.total} pending={pasportCounts?.pending}
                    active={tab === "pasport"} />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="info" className="pt-4 space-y-4">
          {manager && <ProjectStatusStrip proj={proj} eco={eco} live={live} />}
          <ProjectBoard project={proj} canEdit={canEditBoard} onSaved={load} />
          <ProjectInfoTab proj={proj} eco={eco} phases={phases} manager={manager} tasks={tasks}
            canEdit={canEditBoard} onEdit={openEdit} onGoTab={changeTab} onSaved={load} />
        </TabsContent>

        <TabsContent value="timeline" className="pt-4">
          <div className="bg-card border border-border rounded-[20px] p-5">
            {manager && (
              <Dialog open={msOpen} onOpenChange={setMsOpen}>
                <DialogTrigger asChild><Button variant="outline" data-testid="add-milestone-btn" className="mb-4"><Plus size={16} /> Přidat milník</Button></DialogTrigger>
                <DialogContent >
                  <DialogHeader><DialogTitle>Nový milník</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Název</Label><Input data-testid="milestone-title" value={milestone.title} onChange={(e) => setMilestone({ ...milestone, title: e.target.value })} className="h-11 mt-1" placeholder="Např. Předání staveniště" /></div>
                    <div><Label>Datum</Label><Input type="date" data-testid="milestone-date" value={milestone.date} onChange={(e) => setMilestone({ ...milestone, date: e.target.value })} className="h-11 mt-1" /></div>
                    <div><Label>Poznámka</Label><Textarea value={milestone.note} onChange={(e) => setMilestone({ ...milestone, note: e.target.value })} className="mt-1" /></div>
                    <div>
                      <Label>Příloha</Label>
                      <input ref={msFileRef} type="file" onChange={handleMsFile} className="hidden" data-testid="milestone-file-input" />
                      <div className="flex items-center gap-3 mt-1">
                        <Button type="button" variant="outline" onClick={() => msFileRef.current?.click()} disabled={uploading} data-testid="milestone-file-btn" className="h-11">
                          <Paperclip size={16} /> {uploading ? "Nahrávám…" : "Přidat přílohu"}
                        </Button>
                        {milestone.file_url && <span className="text-sm text-emerald-500">Příloha připravena</span>}
                      </div>
                    </div>
                    <Button onClick={() => guardMs(addMilestone)} disabled={msBusy} data-testid="milestone-save" className="w-full h-11 font-bold">{msBusy ? "Ukládám…" : "Přidat"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <div className="relative pl-6 border-l-2 border-border space-y-6" data-testid="timeline">
              {[...history].sort((a, b) => {
                const ca = a.type === "created" || a.title === "Zakázka vytvořena";
                const cb = b.type === "created" || b.title === "Zakázka vytvořena";
                if (ca !== cb) return ca ? 1 : -1;
                return (b.date || "").localeCompare(a.date || "");
              }).map((h) => (
                <div key={h.id} className="relative">
                  <div className={`absolute -left-[31px] w-4 h-4 rounded-full border-2 border-background ${h.type === "milestone" ? "bg-brand" : "bg-muted-foreground"}`} />
                  <div className="flex items-center gap-2">
                    {h.type === "milestone" && <Flag size={14} className="text-brand" />}
                    <span className="font-bold">{h.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(h.date)}</div>
                  {h.note && <p className="text-sm text-muted-foreground mt-1">{h.note}</p>}
                  {h.file_url && <a href={resolveImg(h.file_url)} target="_blank" rel="noreferrer" data-testid="milestone-attachment" className="text-sm text-brand hover:underline flex items-center gap-1 mt-1"><Paperclip size={13} /> Příloha</a>}
                </div>
              ))}
              {history.length === 0 && <p className="text-muted-foreground text-sm">Žádné události.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="schedule" className="pt-4 space-y-4">
          {/* Přepínač „uvnitř karty" (kánon §3): Události = dnešní obsah, Úkoly = kanban/seznam */}
          <div ref={autoPill} className="inline-flex p-1 rounded-full bg-secondary" data-testid="schedule-sub-toggle">
            {(() => {
              // Badge = počet neuzavřených úkolů zakázky (vše kromě Schváleno),
              // nezávislý na rozsahu/filtrech — nesmí odporovat součtům sloupců.
              const openCount = tasks.filter((t) => t.status !== "Schváleno").length;
              return [["events", "Události"], ["tasks", "Úkoly"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => changeSub(v)} data-testid={`schedule-sub-${v}`} data-pill-on={sub === v || undefined}
                  className={`h-9 px-4 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-1.5 ${
                    sub === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                  {l}
                  {v === "tasks" && openCount > 0 && (
                    <span className="rounded-full bg-secondary text-muted-foreground px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
                      {openCount}
                    </span>
                  )}
                </button>
              ));
            })()}
          </div>

          {sub === "tasks" ? (
            <ProjectTasksTab projectId={id} phases={phases} tasks={tasks} team={team}
              narrow={narrow} onReload={load} />
          ) : (
          <div className="bg-card border border-border rounded-[20px] p-5">
            {phases.length > 0 ? (
              <div className="mb-4" data-testid="project-progress">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Celkový postup zakázky</span>
                  <span className="text-sm font-bold text-brand" data-testid="project-progress-pct">{projectProgress(phases, tasks)} %</span>
                </div>
                <Progress value={projectProgress(phases, tasks)} className="h-2.5" />
              </div>
            ) : (
              /* Bez fází se postup zadává ručně — stejná hodnota, jakou ukazuje
                 pruh zakázky v Plánování (POST /projects/{id}/progress). */
              <div className="mb-4" data-testid="project-progress-manual">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Celkový postup zakázky</span>
                  <span className="text-sm font-bold text-brand" data-testid="project-progress-pct">{manProg ?? (proj.progress || 0)} %</span>
                </div>
                {manager ? (
                  <>
                    <Slider value={[manProg ?? (proj.progress || 0)]} min={0} max={100} step={5}
                      onValueChange={(v) => setManProg(v[0])}
                      onValueCommit={async (v) => {
                        try { await api.post(`/projects/${id}/progress`, { progress: v[0] }); setManProg(null); load(); }
                        catch { toast.error("Uložení se nezdařilo"); setManProg(null); }
                      }}
                      data-testid="project-progress-slider" />
                    <p className="text-[12px] text-muted-foreground mt-1.5">Bez fází se postup zadává ručně — posuň a pusť, uloží se samo. Stejné % ukazuje pruh zakázky v Plánování. Jakmile přidáš fáze, postup se počítá z nich.</p>
                  </>
                ) : (
                  <Progress value={proj.progress || 0} className="h-2.5" />
                )}
              </div>
            )}
            {manager && (
              <Button variant="outline" data-testid="add-phase-btn" className="mb-4"
                onClick={() => { setPhaseForm({ name: "", start_date: "", end_date: "", progress: 0, depends_on: "", responsible_ids: [] }); setPhaseOpen(true); }}>
                <Plus size={16} /> Přidat fázi
              </Button>
            )}
            <InteractiveGantt phases={phases} tasks={tasks} editable={manager} onSave={savePhaseDates}
              onOpenPhase={manager ? ((phid) => { const ph = phases.find((p) => p.id === phid); if (ph) openEditPhase(ph); }) : undefined}
              onCreate={manager ? ((start_date, end_date) => { setPhaseForm({ name: "", start_date, end_date, progress: 0, depends_on: "", responsible_ids: [] }); setPhaseOpen(true); }) : undefined} />
            <div className="mt-6 space-y-3">
              {phases.map((ph) => {
                const phaseTasks = tasks.filter((t) => t.phase_id === ph.id);
                const stats = phaseTaskStats(ph.id, tasks);
                const prog = effProgress(ph, tasks);
                const done = prog >= 100;
                const depName = ph.depends_on ? (phases.find((x) => x.id === ph.depends_on) || {}).name : null;
                return (
                <div key={ph.id} className="border border-border rounded-[20px] p-3" data-testid={`phase-${ph.id}`}>
                  <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
                    <span className="font-semibold flex items-center gap-2">
                      {!ph.responsible_id && <AlertTriangle size={15} className="text-amber-500 shrink-0" data-testid={`phase-no-resp-${ph.id}`} />}
                      {ph.name}
                      {done && <span className="text-[11px] font-bold uppercase text-emerald-500" data-testid={`phase-done-${ph.id}`}>hotovo</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{formatDate(ph.start_date)} – {formatDate(ph.end_date)}</span>
                      {manager && <button onClick={() => openEditPhase(ph)} data-testid={`edit-phase-${ph.id}`} className="text-muted-foreground hover:text-brand"><Pencil size={15} /></button>}
                      {manager && <button onClick={() => deletePhase(ph.id)} data-testid={`delete-phase-${ph.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>}
                    </div>
                  </div>
                  <div className="text-xs mb-1.5" data-testid={`phase-resp-${ph.id}`}>
                    {(() => {
                      const ids = ph.responsible_ids || (ph.responsible_id ? [ph.responsible_id] : []);
                      const names = ids.map((rid) => respName(rid)).filter(Boolean);
                      return names.length
                        ? <span className="text-muted-foreground">{names.length > 1 ? "Osoby" : "Odpovědná osoba"}: <span className="font-semibold text-foreground">{names.join(", ")}</span></span>
                        : <button onClick={() => manager && openEditPhase(ph)} className="text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1 hover:underline"><AlertTriangle size={12} /> Chybí odpovědná osoba{manager ? " — doplnit" : ""}</button>;
                    })()}
                  </div>
                  {depName && <div className="text-xs text-muted-foreground mb-1.5">Závisí na: <span className="font-semibold text-foreground">{depName}</span></div>}
                  <Progress value={prog} className="h-2" />
                  <div className="text-xs text-muted-foreground mt-1">
                    {prog}% hotovo{stats.total > 0 ? ` · počítáno z úkolů (${stats.done}/${stats.total})` : " · ručně"}
                  </div>

                  <div className="mt-3 space-y-1.5">
                    {phaseTasks.map((t) => (
                      <button key={t.id} onClick={() => { setModalTaskIds(phaseTasks.map((x) => x.id)); setModalTask(t.id); }} data-testid={`phase-task-${t.id}`}
                        className="w-full flex items-center justify-between gap-2 text-left p-2 bg-secondary rounded-sm hover:bg-muted transition-colors">
                        <span className="flex items-center gap-2 text-sm min-w-0">
                          <ClipboardList size={14} className="shrink-0 text-muted-foreground" />
                          <span className="truncate">{t.title}</span>
                        </span>
                        <TaskStatusBadge value={t.status} />
                      </button>
                    ))}
                    {manager && (
                      <Button variant="ghost" size="sm" onClick={() => openAddTask(ph.id)} data-testid={`add-phase-task-${ph.id}`}
                        className="text-brand hover:text-brand h-8 px-2"><Plus size={14} /> Přidat úkol</Button>
                    )}
                  </div>
                </div>
              );})}
            </div>
          </div>
          )}

          {/* Phase form dialogs (shared) */}
          <PhaseFormDialog open={phaseEditOpen} onOpenChange={setPhaseEditOpen} mode="edit"
            projectName={proj.name} form={phaseEditForm} setForm={setPhaseEditForm}
            responsibles={responsibles} phases={phases}
            taskStats={phaseEditForm.id ? phaseTaskStats(phaseEditForm.id, tasks) : undefined}
            tasks={phaseEditForm.id ? tasks.filter((t) => t.phase_id === phaseEditForm.id) : []}
            onOpenTask={(t) => { setPhaseEditOpen(false); nav(`/ukoly/${t.id}`); }}
            onAddTask={manager ? () => { setPhaseEditOpen(false); openAddTask(phaseEditForm.id); } : undefined}
            onDelete={manager ? () => { setPhaseEditOpen(false); deletePhase(phaseEditForm.id); } : undefined}
            onSubmit={savePhaseEdit} />
          <PhaseFormDialog open={phaseOpen} onOpenChange={setPhaseOpen} mode="create"
            projectName={proj.name} form={phaseForm} setForm={setPhaseForm}
            responsibles={responsibles} phases={phases} onSubmit={addPhase} />
        </TabsContent>

        <TabsContent value="team" className="pt-4">
          {manager && (
            <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
              <DialogContent >
                <DialogHeader><DialogTitle>Přiřadit členy</DialogTitle></DialogHeader>
                {team.length > 0 && (
                  <div className="flex items-center justify-between gap-2 flex-wrap pb-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">
                      Vybráno {(proj.team || []).length} z {team.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={() => setAllTeam(true)} data-testid="team-select-all"
                        disabled={teamBusy || (proj.team || []).length === team.length}
                        className="h-9 font-bold">Přidat všechny</Button>
                      <Button variant="ghost" onClick={() => setAllTeam(false)} data-testid="team-clear-all"
                        disabled={teamBusy || (proj.team || []).length === 0}
                        className="h-9">Odebrat všechny</Button>
                    </div>
                  </div>
                )}
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {team.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-secondary rounded-sm cursor-pointer">
                      <Checkbox checked={(proj.team || []).includes(u.id)} onCheckedChange={(c) => toggleTeam(u.id, c)} data-testid={`team-check-${u.id}`} />
                      <span>{u.name} <span className="text-muted-foreground text-sm">· {u.role === "OWNER" ? "Majitel" : u.role === "MANAGER" ? "Stavbyvedoucí" : u.role === "DESIGNER" ? "Projektant" : "Dělník"}</span></span>
                    </label>
                  ))}
                  {team.length === 0 && <p className="text-sm text-muted-foreground">Ve firmě zatím nejsou žádní pracovníci.</p>}
                </div>
              </DialogContent>
            </Dialog>
          )}
          <ProjectTeamTab projectId={id} proj={proj} eco={eco} user={user} manager={manager}
            teamAll={team} onManage={() => setTeamOpen(true)} onRemove={(uid) => toggleTeam(uid, false)} onReload={load} />
        </TabsContent>

        <TabsContent value="docs" className="pt-4" data-testid="project-docs-tab">
          <ProjectDocsTab projectId={id} manager={manager} />
        </TabsContent>

        <TabsContent value="pasport" className="pt-4" data-testid="project-pasport-tab">
          <ProjectPasportTab projectId={id} onCounts={setPasportCounts} />
        </TabsContent>

        {reader && (
          <TabsContent value="attendance" className="pt-4">
            <ProjectAttendanceTab projectId={id} readOnly={designer} />
          </TabsContent>
        )}

        {reader && (
          <TabsContent value="diary" className="pt-4" data-testid="project-diary-tab">
            <ProjectDiary projectId={id} />
          </TabsContent>
        )}

        {manager && (
          <TabsContent value="budget" className="pt-4" data-testid="project-budget-tab">
            <ProjectBudget projectId={id} />
          </TabsContent>
        )}

        {manager && (
          <TabsContent value="eco" className="pt-4">
            <ProjectEconomics projectId={id} />
          </TabsContent>
        )}
      </Tabs>

      {/* Add task to phase dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="phase-task-dialog">
          <DialogHeader><DialogTitle>Nový úkol k fázi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Název *</Label><Input data-testid="phase-task-title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="h-11 mt-1" placeholder="Např. Objednat kontejnery" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Termín</Label><Input type="date" value={taskForm.deadline} onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })} className="h-11 mt-1" /></div>
              <div>
                <Label>Priorita</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Nízká", "Střední", "Vysoká"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Přiřadit</Label>
              <div className="space-y-1 mt-1 max-h-40 overflow-y-auto border border-border rounded-[20px] p-2">
                {team.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-secondary rounded-sm cursor-pointer">
                    <Checkbox checked={taskForm.assignees.includes(u.id)} onCheckedChange={() => toggleTaskAssignee(u.id)} data-testid={`phase-task-assignee-${u.id}`} />
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={() => guardPt(submitTask)} disabled={ptBusy} data-testid="phase-task-save" className="w-full h-12 font-bold">{ptBusy ? "Ukládám…" : "Přidat úkol"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <TaskDetailModal taskId={modalTask} taskIds={modalTaskIds} onNavigate={setModalTask} open={!!modalTask} onOpenChange={(o) => !o && setModalTask(null)} onChanged={load} />
    </div>
  );
}
